import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { nacService, NodeStatus1Item, NodeStatus2Item, NodeStatus3Item, NodeStatus4Item } from '../services/nac.service';
import ReactMarkdown from 'react-markdown';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './NacReportPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface NacReportPageProps {}

const NacReportPage: React.FC<NacReportPageProps> = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>('summary');
  const [isLoading, setIsLoading] = useState(false);
  const [isPublished, setIsPublished] = useState<boolean>(false);
  const [summaryContent, setSummaryContent] = useState<string>('');
  
  // 노드현황 데이터
  const [nodeStatus1, setNodeStatus1] = useState<NodeStatus1Item[]>([]);
  const [nodeStatus2, setNodeStatus2] = useState<NodeStatus2Item[]>([]);
  const [nodeStatus3, setNodeStatus3] = useState<NodeStatus3Item[]>([]);
  const [nodeStatus4, setNodeStatus4] = useState<NodeStatus4Item[]>([]);
  
  // 비고 데이터
  const [statNotes1, setStatNotes1] = useState<Record<string, string>>({});
  const [statNotes2, setStatNotes2] = useState<Record<string, string>>({});
  const [statNotes3, setStatNotes3] = useState<Record<string, string>>({});
  const [statNotes4, setStatNotes4] = useState<Record<string, string>>({});
  
  // 편집 중인 비고 (인라인 편집용)
  const [editingNotes, setEditingNotes] = useState<Record<string, { statType: string; statKey: string; note: string }>>({});
  const [savingNotes, setSavingNotes] = useState<Record<string, boolean>>({});

  const isAdminOrEditor = user?.role === 'admin' || user?.role === 'editor';

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPeriod && selectedCompany) {
      loadReportData(selectedPeriod, selectedCompany);
      checkPublishStatus(selectedPeriod, selectedCompany);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedCompany]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const companies = await nacService.getPermittedCompanies();
      console.log('Loaded companies:', companies); // 디버깅용
      setAvailableCompanies(companies);
      
      // 관리자/에디터/리뷰어는 회사 선택 후 기간 선택
      // viewer는 자동 선택
      if (user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') {
        // 관리자/에디터/리뷰어는 회사 선택을 기다림
        if (companies.length === 1) {
          setSelectedCompany(companies[0]);
        }
      } else {
        // viewer는 자동 선택
        if (companies.length > 0) {
          setSelectedCompany(companies[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      alert('데이터를 불러오는데 실패했습니다: ' + (error as any)?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      loadPeriods();
    } else {
      setAvailablePeriods([]);
      setSelectedPeriod('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const loadPeriods = async () => {
    if (!selectedCompany) return;
    
    try {
      const periods = await nacService.getAvailablePeriods();
      setAvailablePeriods(periods);

      if (periods.length > 0) {
        setSelectedPeriod(periods[0]);
      }
    } catch (error) {
      console.error('Failed to load periods:', error);
    }
  };

  const loadReportData = async (period: string, company: string) => {
    if (!period || !company) return;
    
    setIsLoading(true);
    try {
      // 요약 내용 로드
      const summary = await nacService.getSummary(period, company);
      setSummaryContent(summary?.summaryContent || '');

      // 전월 period 계산
      const year = parseInt(period.slice(0, 4));
      const month = parseInt(period.slice(4));
      const prevDate = new Date(year, month - 2, 1);
      const prevPeriod = `${prevDate.getFullYear()}${String(prevDate.getMonth() + 1).padStart(2, '0')}`;

      // 노드현황 데이터 로드
      const [status1, status2, status3, status4] = await Promise.all([
        nacService.getNodeStatus1(period, company, prevPeriod),
        nacService.getNodeStatus2(period, company, prevPeriod),
        nacService.getNodeStatus3(period, company, prevPeriod),
        nacService.getNodeStatus4(period, company, prevPeriod),
      ]);

      setNodeStatus1(status1);
      setNodeStatus2(status2);
      setNodeStatus3(status3);
      setNodeStatus4(status4);

      // 비고 데이터 로드
      const [notes1, notes2, notes3, notes4] = await Promise.all([
        nacService.getStatNotes(period, company, 'node_status_1'),
        nacService.getStatNotes(period, company, 'node_status_2'),
        nacService.getStatNotes(period, company, 'node_status_3'),
        nacService.getStatNotes(period, company, 'node_status_4'),
      ]);

      setStatNotes1(notes1);
      setStatNotes2(notes2);
      setStatNotes3(notes3);
      setStatNotes4(notes4);
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkPublishStatus = async (period: string, company: string) => {
    if (!period || !company) return;
    
    try {
      const published = await nacService.getPublishStatus(period, company);
      setIsPublished(published);
    } catch (error) {
      console.error('Failed to check publish status:', error);
      setIsPublished(false);
    }
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
  };

  const handleCompanyChange = (company: string) => {
    setSelectedCompany(company);
  };


  // 화살표 아이콘 렌더링 함수
  const renderDiffArrow = (diffText: string) => {
    if (diffText.includes('증가') || diffText.includes('+')) {
      const match = diffText.match(/([+-]?\d+)/);
      const value = match ? match[1] : '';
      return (
        <span className="diff-arrow diff-increase">
          <span className="arrow-icon">🔺</span>
          <span className="diff-value">{value}</span>
        </span>
      );
    } else if (diffText.includes('감소') || diffText.includes('-')) {
      const match = diffText.match(/([+-]?\d+)/);
      const value = match ? match[1] : '';
      return (
        <span className="diff-arrow diff-decrease">
          <span className="arrow-icon">🔻</span>
          <span className="diff-value">{value}</span>
        </span>
      );
    } else {
      return (
        <span className="diff-arrow diff-same">
          <span className="arrow-icon">➖</span>
          <span className="diff-value">0</span>
        </span>
      );
    }
  };

  // 비고 인라인 편집 시작
  const handleNoteEdit = (statType: string, statKey: string) => {
    const notes = statType === 'node_status_1' ? statNotes1 :
                  statType === 'node_status_2' ? statNotes2 :
                  statType === 'node_status_3' ? statNotes3 : statNotes4;
    const noteKey = `${statType}_${statKey}`;
    setEditingNotes({
      ...editingNotes,
      [noteKey]: { statType, statKey, note: notes[statKey] || '' }
    });
  };

  // 비고 인라인 편집 값 변경
  const handleNoteChange = (noteKey: string, value: string) => {
    setEditingNotes({
      ...editingNotes,
      [noteKey]: { ...editingNotes[noteKey], note: value }
    });
  };

  // 비고 저장
  const handleNoteSave = async (noteKey: string) => {
    const editing = editingNotes[noteKey];
    if (!editing || !selectedPeriod || !selectedCompany) return;
    
    setSavingNotes({ ...savingNotes, [noteKey]: true });
    try {
      await nacService.saveStatNote(
        selectedPeriod,
        selectedCompany,
        editing.statType,
        editing.statKey,
        editing.note,
      );
      
      // 비고 상태 업데이트
      const notes = editing.statType === 'node_status_1' ? statNotes1 :
                    editing.statType === 'node_status_2' ? statNotes2 :
                    editing.statType === 'node_status_3' ? statNotes3 : statNotes4;
      
      const updatedNotes = { ...notes, [editing.statKey]: editing.note };
      
      if (editing.statType === 'node_status_1') setStatNotes1(updatedNotes);
      else if (editing.statType === 'node_status_2') setStatNotes2(updatedNotes);
      else if (editing.statType === 'node_status_3') setStatNotes3(updatedNotes);
      else setStatNotes4(updatedNotes);
      
      // 편집 상태 제거
      const newEditingNotes = { ...editingNotes };
      delete newEditingNotes[noteKey];
      setEditingNotes(newEditingNotes);
      
      alert('저장되었습니다.');
    } catch (error) {
      console.error('Failed to save note:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setSavingNotes({ ...savingNotes, [noteKey]: false });
    }
  };

  // 비고 편집 취소
  const handleNoteCancel = (noteKey: string) => {
    const newEditingNotes = { ...editingNotes };
    delete newEditingNotes[noteKey];
    setEditingNotes(newEditingNotes);
  };

  const tabs = [
    { id: 'summary', label: '요약' },
    { id: 'policy', label: 'NAC 정책' },
    { id: 'node-status-1', label: '노드현황 1' },
    { id: 'node-status-2', label: '노드현황 2' },
    { id: 'node-status-3', label: '노드현황 3' },
    { id: 'node-status-4', label: '노드현황 4' },
  ];

  return (
    <div className="nac-report-page">
      <div className="page-header">
        <h1>NAC 운영보고서</h1>
        <div className="header-controls">
          {/* 관리자/에디터/리뷰어는 회사 선택 먼저 */}
          {(user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') && (
            <div className="filter-group">
              <label htmlFor="company-select">회사:</label>
              <select
                id="company-select"
                value={selectedCompany}
                onChange={(e) => handleCompanyChange(e.target.value)}
                disabled={isLoading}
              >
                <option value="">선택하세요</option>
                {availableCompanies.map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>
              {availableCompanies.length === 0 && !isLoading && (
                <span style={{ color: 'red', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                  회사 목록이 없습니다
                </span>
              )}
            </div>
          )}
          <div className="filter-group">
            <label htmlFor="period-select">기간:</label>
            <select
              id="period-select"
              value={selectedPeriod}
              onChange={(e) => handlePeriodChange(e.target.value)}
              disabled={!selectedCompany || isLoading}
            >
              <option value="">{selectedCompany ? '선택하세요' : '회사를 먼저 선택하세요'}</option>
              {availablePeriods.map((period) => (
                <option key={period} value={period}>
                  {period.slice(0, 4)}년 {period.slice(4)}월
                </option>
              ))}
            </select>
            {selectedCompany && availablePeriods.length === 0 && !isLoading && (
              <span style={{ color: 'red', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                기간 데이터가 없습니다
              </span>
            )}
          </div>
        </div>
      </div>

      {!isPublished && selectedPeriod && selectedCompany && (
        <div className="publish-warning">
          <p>⚠️ 이 보고서는 아직 발행되지 않았습니다. 관리자에게 문의하세요.</p>
        </div>
      )}

      {isLoading ? (
        <div className="loading">로딩 중...</div>
      ) : !selectedPeriod ? (
        <div className="empty-state">
          <p>기간을 선택해주세요.</p>
        </div>
      ) : !selectedCompany ? (
        <div className="empty-state">
          <p>회사를 선택해주세요.</p>
        </div>
      ) : (
        <>
          <div className="tab-container">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === 'summary' && (
              <div className="summary-section">
                <div className="summary-view">
                  {summaryContent ? (
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => {
                          const href = props.href || '';
                          
                          // 외부 링크인 경우 새 탭에서 열기
                          return (
                            <a 
                              {...props} 
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#3498db', textDecoration: 'underline' }}
                            >
                              {props.children || href}
                            </a>
                          );
                        },
                      }}
                    >
                      {summaryContent}
                    </ReactMarkdown>
                  ) : (
                    <p>요약 내용이 없습니다.</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'policy' && (
              <div className="policy-section">
                <h2>NAC 기본 정책</h2>
                <table className="policy-table">
                  <thead>
                    <tr>
                      <th>NO</th>
                      <th>기준 정책</th>
                      <th>설명</th>
                      <th>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>1</td>
                      <td>NAC 에이전트 미설치 차단 (무선)</td>
                      <td>NAC 미설치시 차단되는 정책 (intel/samsung Electronics)</td>
                      <td>-</td>
                    </tr>
                    <tr>
                      <td>2</td>
                      <td>NAC 에이전트 미설치 차단</td>
                      <td>NAC 미설치시 차단되는 정책</td>
                      <td>-</td>
                    </tr>
                    <tr>
                      <td>3</td>
                      <td>DLP 에이전트 미설치 차단</td>
                      <td>DLP 미설치시 차단되는 정책</td>
                      <td>-</td>
                    </tr>
                    <tr>
                      <td>4</td>
                      <td>기본정책</td>
                      <td>모든 설치 조건을 만족하는 정책</td>
                      <td>-</td>
                    </tr>
                  </tbody>
                </table>

                <h2>NAC 예외 정책</h2>
                <table className="policy-table">
                  <thead>
                    <tr>
                      <th>NO</th>
                      <th>설명</th>
                      <th>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>1</td>
                      <td>NAC를 설치하지 않은 정책 (서버 및 설치 예외 노드)</td>
                      <td>-</td>
                    </tr>
                    <tr>
                      <td>2</td>
                      <td>NAC설치를 진행하지만, 인증은 하지않는 노드</td>
                      <td>-</td>
                    </tr>
                    <tr>
                      <td>3</td>
                      <td>NAC 인증을 일주일 1회 하는 노드</td>
                      <td>-</td>
                    </tr>
                    <tr>
                      <td>4</td>
                      <td>모든 설치 조건을 만족하는 정책</td>
                      <td>-</td>
                    </tr>
                  </tbody>
                </table>
                <p>
                  ※ <a href="https://portal.seah.co.kr/dlp/exception-request" target="_blank" rel="noopener noreferrer">예외 정책 신청 프로세스 바로가기</a>
                </p>
              </div>
            )}

            {activeTab === 'node-status-1' && (
              <div className="node-status-section">
                <h2>NAC 전체노드</h2>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>노드 구분</th>
                      <th>금월</th>
                      <th>전월</th>
                      <th>전월 비교</th>
                      <th>비율</th>
                      <th>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodeStatus1.map((item, index) => (
                      <tr key={index} className={item.node_type === '합계' ? 'total-row' : ''}>
                        <td>{item.node_type}</td>
                        <td>{item.current_count.toLocaleString()}</td>
                        <td>{item.prev_count.toLocaleString()}</td>
                        <td>{renderDiffArrow(item.diff_text)}</td>
                        <td>{item.ratio}%</td>
                        <td>
                          {isAdminOrEditor ? (() => {
                            const noteKey = `node_status_1_${item.node_type}`;
                            const editing = editingNotes[noteKey];
                            const currentNote = statNotes1[item.node_type] || '';
                            
                            if (editing) {
                              return (
                                <div className="note-cell-editing">
                                  <input
                                    type="text"
                                    value={editing.note}
                                    onChange={(e) => handleNoteChange(noteKey, e.target.value)}
                                    className="note-input"
                                    placeholder="비고를 입력하세요"
                                    autoFocus
                                  />
                                  {editing.note.trim().length > 0 && (
                                    <div className="note-actions">
                                      <button
                                        className="note-save-button"
                                        onClick={() => handleNoteSave(noteKey)}
                                        disabled={savingNotes[noteKey]}
                                      >
                                        {savingNotes[noteKey] ? '저장 중...' : '저장'}
                                      </button>
                                      <button
                                        className="note-cancel-button"
                                        onClick={() => handleNoteCancel(noteKey)}
                                        disabled={savingNotes[noteKey]}
                                      >
                                        취소
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            return (
                              <div className="note-cell">
                                <span onClick={() => handleNoteEdit('node_status_1', item.node_type)} style={{ cursor: 'pointer' }}>
                                  {currentNote || '-'}
                                </span>
                              </div>
                            );
                          })() : (
                            <span>{statNotes1[item.node_type] || '-'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* 가로형 바 차트 - 금월과 전월 비교 */}
                {nodeStatus1.length > 0 && (() => {
                  // 데이터가 있는 항목만 필터링 (합계 제외, current_count > 0 || prev_count > 0)
                  const chartData = nodeStatus1.filter(item => item.node_type !== '합계' && (item.current_count > 0 || item.prev_count > 0));
                  const pcItem = chartData.find(item => item.node_type === 'PC');
                  const otherItems = chartData.filter(item => item.node_type !== 'PC');
                  const sortedData = pcItem ? [pcItem, ...otherItems] : chartData;
                  const chartHeight = Math.max(400, sortedData.length * 40 + 100);
                  
                  return (
                    <div className="chart-container" style={{ marginTop: '30px', height: `${chartHeight}px` }}>
                      <h3>NAC 전체노드 (금월 vs 전월)</h3>
                      <Bar
                        data={{
                          labels: sortedData.map(item => item.node_type),
                          datasets: [
                            {
                              label: '금월',
                              data: sortedData.map(item => item.current_count),
                              backgroundColor: 'rgba(52, 152, 219, 0.8)',
                              borderColor: 'rgba(52, 152, 219, 1)',
                              borderWidth: 1,
                            },
                            {
                              label: '전월',
                              data: sortedData.map(item => item.prev_count),
                              backgroundColor: 'rgba(149, 165, 166, 0.8)',
                              borderColor: 'rgba(149, 165, 166, 1)',
                              borderWidth: 1,
                            },
                          ],
                        }}
                        options={{
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          layout: {
                            padding: {
                              left: 10,
                              right: 10,
                            },
                          },
                          plugins: {
                            legend: {
                              position: 'top' as const,
                            },
                            title: {
                              display: false,
                            },
                          },
                          scales: {
                            x: {
                              beginAtZero: true,
                            },
                            y: {
                              ticks: {
                                autoSkip: false,
                                maxRotation: 0,
                                minRotation: 0,
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === 'node-status-2' && (
              <div className="node-status-section">
                <h2>PC별 정책 현황</h2>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>정책명</th>
                      <th>금월</th>
                      <th>전월</th>
                      <th>전월 비교</th>
                      <th>비율</th>
                      <th>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodeStatus2.map((item, index) => (
                      <tr key={index} className={item.policy_name === '합계' ? 'total-row' : ''}>
                        <td>{item.policy_name}</td>
                        <td>{item.current_count.toLocaleString()}</td>
                        <td>{item.prev_count.toLocaleString()}</td>
                        <td>{renderDiffArrow(item.diff_text)}</td>
                        <td>{item.ratio}%</td>
                        <td>
                          {isAdminOrEditor ? (() => {
                            const noteKey = `node_status_2_${item.policy_name}`;
                            const editing = editingNotes[noteKey];
                            const currentNote = statNotes2[item.policy_name] || '';
                            
                            if (editing) {
                              return (
                                <div className="note-cell-editing">
                                  <input
                                    type="text"
                                    value={editing.note}
                                    onChange={(e) => handleNoteChange(noteKey, e.target.value)}
                                    className="note-input"
                                    placeholder="비고를 입력하세요"
                                    autoFocus
                                  />
                                  {editing.note.trim().length > 0 && (
                                    <div className="note-actions">
                                      <button
                                        className="note-save-button"
                                        onClick={() => handleNoteSave(noteKey)}
                                        disabled={savingNotes[noteKey]}
                                      >
                                        {savingNotes[noteKey] ? '저장 중...' : '저장'}
                                      </button>
                                      <button
                                        className="note-cancel-button"
                                        onClick={() => handleNoteCancel(noteKey)}
                                        disabled={savingNotes[noteKey]}
                                      >
                                        취소
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            return (
                              <div className="note-cell">
                                <span onClick={() => handleNoteEdit('node_status_2', item.policy_name)} style={{ cursor: 'pointer' }}>
                                  {currentNote || '-'}
                                </span>
                              </div>
                            );
                          })() : (
                            <span>{statNotes2[item.policy_name] || '-'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* 가로형 바 차트 - 금월과 전월 비교 */}
                {nodeStatus2.length > 0 && (() => {
                  // 데이터가 있는 항목만 필터링 (합계 제외, current_count > 0 || prev_count > 0)
                  const chartData = nodeStatus2.filter(item => item.policy_name !== '합계' && (item.current_count > 0 || item.prev_count > 0));
                  const chartHeight = Math.max(400, chartData.length * 40 + 100);
                  return (
                    <div className="chart-container" style={{ marginTop: '30px', height: `${chartHeight}px` }}>
                      <h3>PC별 정책 현황 (금월 vs 전월)</h3>
                      <Bar
                        data={{
                          labels: chartData.map(item => item.policy_name),
                          datasets: [
                            {
                              label: '금월',
                              data: chartData.map(item => item.current_count),
                              backgroundColor: 'rgba(52, 152, 219, 0.8)',
                              borderColor: 'rgba(52, 152, 219, 1)',
                              borderWidth: 1,
                            },
                            {
                              label: '전월',
                              data: chartData.map(item => item.prev_count),
                              backgroundColor: 'rgba(149, 165, 166, 0.8)',
                              borderColor: 'rgba(149, 165, 166, 1)',
                              borderWidth: 1,
                            },
                          ],
                        }}
                        options={{
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          layout: {
                            padding: {
                              left: 10,
                              right: 10,
                            },
                          },
                          plugins: {
                            legend: {
                              position: 'top' as const,
                            },
                            title: {
                              display: false,
                            },
                          },
                          scales: {
                            x: {
                              beginAtZero: true,
                            },
                            y: {
                              ticks: {
                                autoSkip: false,
                                maxRotation: 0,
                                minRotation: 0,
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === 'node-status-3' && (
              <div className="node-status-section">
                <h2>PC별 운영체제 요약</h2>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>현황</th>
                      <th>금월</th>
                      <th>전월</th>
                      <th>전월 비교</th>
                      <th>비율</th>
                      <th>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodeStatus3.map((item, index) => (
                      <tr key={index} className={item.os_category === '합계' ? 'total-row' : ''}>
                        <td>{item.os_category}</td>
                        <td>{item.current_count.toLocaleString()}</td>
                        <td>{item.prev_count.toLocaleString()}</td>
                        <td>{renderDiffArrow(item.diff_text)}</td>
                        <td>{item.ratio}%</td>
                        <td>
                          {isAdminOrEditor ? (() => {
                            const noteKey = `node_status_3_${item.os_category}`;
                            const editing = editingNotes[noteKey];
                            const currentNote = statNotes3[item.os_category] || '';
                            
                            if (editing) {
                              return (
                                <div className="note-cell-editing">
                                  <input
                                    type="text"
                                    value={editing.note}
                                    onChange={(e) => handleNoteChange(noteKey, e.target.value)}
                                    className="note-input"
                                    placeholder="비고를 입력하세요"
                                    autoFocus
                                  />
                                  {editing.note.trim().length > 0 && (
                                    <div className="note-actions">
                                      <button
                                        className="note-save-button"
                                        onClick={() => handleNoteSave(noteKey)}
                                        disabled={savingNotes[noteKey]}
                                      >
                                        {savingNotes[noteKey] ? '저장 중...' : '저장'}
                                      </button>
                                      <button
                                        className="note-cancel-button"
                                        onClick={() => handleNoteCancel(noteKey)}
                                        disabled={savingNotes[noteKey]}
                                      >
                                        취소
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            return (
                              <div className="note-cell">
                                <span onClick={() => handleNoteEdit('node_status_3', item.os_category)} style={{ cursor: 'pointer' }}>
                                  {currentNote || '-'}
                                </span>
                              </div>
                            );
                          })() : (
                            <span>{statNotes3[item.os_category] || '-'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* 가로형 바 차트 - 금월과 전월 비교 */}
                {nodeStatus3.length > 0 && (() => {
                  // 데이터가 있는 항목만 필터링 (합계 제외, current_count > 0 || prev_count > 0)
                  const chartData = nodeStatus3.filter(item => item.os_category !== '합계' && (item.current_count > 0 || item.prev_count > 0));
                  const chartHeight = Math.max(300, chartData.length * 40 + 100);
                  return (
                    <div className="chart-container" style={{ marginTop: '30px', height: `${chartHeight}px` }}>
                      <h3>PC별 운영체제 요약 (금월 vs 전월)</h3>
                      <Bar
                        data={{
                          labels: chartData.map(item => item.os_category),
                          datasets: [
                            {
                              label: '금월',
                              data: chartData.map(item => item.current_count),
                              backgroundColor: 'rgba(52, 152, 219, 0.8)',
                              borderColor: 'rgba(52, 152, 219, 1)',
                              borderWidth: 1,
                            },
                            {
                              label: '전월',
                              data: chartData.map(item => item.prev_count),
                              backgroundColor: 'rgba(149, 165, 166, 0.8)',
                              borderColor: 'rgba(149, 165, 166, 1)',
                              borderWidth: 1,
                            },
                          ],
                        }}
                        options={{
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          layout: {
                            padding: {
                              left: 10,
                              right: 10,
                            },
                          },
                          plugins: {
                            legend: {
                              position: 'top' as const,
                            },
                            title: {
                              display: false,
                            },
                          },
                          scales: {
                            x: {
                              beginAtZero: true,
                            },
                            y: {
                              ticks: {
                                autoSkip: false,
                                maxRotation: 0,
                                minRotation: 0,
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === 'node-status-4' && (
              <div className="node-status-section">
                <h2>운영체제별 PC 노드</h2>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>운영체제</th>
                      <th>금월</th>
                      <th>전월</th>
                      <th>전월 비교</th>
                      <th>비율</th>
                      <th>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodeStatus4.map((item, index) => (
                      <tr key={index} className={item.platform === '합계' ? 'total-row' : ''}>
                        <td>{item.platform}</td>
                        <td>{item.current_count.toLocaleString()}</td>
                        <td>{item.prev_count.toLocaleString()}</td>
                        <td>{renderDiffArrow(item.diff_text)}</td>
                        <td>{item.ratio}%</td>
                        <td>
                          {isAdminOrEditor ? (() => {
                            const noteKey = `node_status_4_${item.platform}`;
                            const editing = editingNotes[noteKey];
                            const currentNote = statNotes4[item.platform] || '';
                            
                            if (editing) {
                              return (
                                <div className="note-cell-editing">
                                  <input
                                    type="text"
                                    value={editing.note}
                                    onChange={(e) => handleNoteChange(noteKey, e.target.value)}
                                    className="note-input"
                                    placeholder="비고를 입력하세요"
                                    autoFocus
                                  />
                                  {editing.note.trim().length > 0 && (
                                    <div className="note-actions">
                                      <button
                                        className="note-save-button"
                                        onClick={() => handleNoteSave(noteKey)}
                                        disabled={savingNotes[noteKey]}
                                      >
                                        {savingNotes[noteKey] ? '저장 중...' : '저장'}
                                      </button>
                                      <button
                                        className="note-cancel-button"
                                        onClick={() => handleNoteCancel(noteKey)}
                                        disabled={savingNotes[noteKey]}
                                      >
                                        취소
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            return (
                              <div className="note-cell">
                                <span onClick={() => handleNoteEdit('node_status_4', item.platform)} style={{ cursor: 'pointer' }}>
                                  {currentNote || '-'}
                                </span>
                              </div>
                            );
                          })() : (
                            <span>{statNotes4[item.platform] || '-'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* 가로형 바 차트 - 금월과 전월 비교 */}
                {nodeStatus4.length > 0 && (() => {
                  // 데이터가 있는 항목만 필터링 (합계 제외, current_count > 0 || prev_count > 0)
                  const chartData = nodeStatus4.filter(item => item.platform !== '합계' && (item.current_count > 0 || item.prev_count > 0));
                  const chartHeight = Math.max(600, chartData.length * 40 + 100);
                  return (
                    <div className="chart-container" style={{ marginTop: '30px', height: `${chartHeight}px` }}>
                      <h3>운영체제별 PC 노드 (금월 vs 전월)</h3>
                      <Bar
                        data={{
                          labels: chartData.map(item => item.platform),
                          datasets: [
                            {
                              label: '금월',
                              data: chartData.map(item => item.current_count),
                              backgroundColor: 'rgba(52, 152, 219, 0.8)',
                              borderColor: 'rgba(52, 152, 219, 1)',
                              borderWidth: 1,
                            },
                            {
                              label: '전월',
                              data: chartData.map(item => item.prev_count),
                              backgroundColor: 'rgba(149, 165, 166, 0.8)',
                              borderColor: 'rgba(149, 165, 166, 1)',
                              borderWidth: 1,
                            },
                          ],
                        }}
                        options={{
                          indexAxis: 'y',
                          responsive: true,
                          maintainAspectRatio: false,
                          layout: {
                            padding: {
                              left: 10,
                              right: 10,
                            },
                          },
                          plugins: {
                            legend: {
                              position: 'top' as const,
                            },
                            title: {
                              display: false,
                            },
                          },
                          scales: {
                            x: {
                              beginAtZero: true,
                            },
                            y: {
                              ticks: {
                                autoSkip: false,
                                maxRotation: 0,
                                minRotation: 0,
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </>
      )}

    </div>
  );
};

export default NacReportPage;
